import { useStyles } from '@archipro-design/aria';
import { useEffect, useRef, useState } from 'react';
import * as S from '@modules/professional/component/page-layout/PageLayout.style';
import {
    ContentWrapper,
    MapSection,
} from '@modules/professional/component/contact-tab/ContactPage.mobile.style';
import { FindNearest } from '@modules/professional/component/contact-tab/FindNearest';

import ListMapNavTab from './ListMapNavTab';
import { LocationListMobile } from './LocationList.mobile';
import { LocationMap } from '@modules/professional/component/contact-tab/LocationMap';
import { useContactLocationMap } from '@modules/professional/hook/use-contact-location-map';
import { useLoaderData, useSearchParams } from 'react-router';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { getLinkedProfileLocations } from '@modules/professional/util/contact-page-helper';
import { NoSearchResult } from '@modules/professional/component/contact-tab/NoSearchResult';
import {
    MOBILE_SEARCH_MAP_OFFSET,
    SCROLL_TO_MAP_AFTER_CLICK_MAP_BUTTON,
} from '@modules/professional/component/contact-tab/LocationConstant';
import type { loader } from '@modules/professional/page/loaders/contact-page-loader.server';
import type { ContactLoaderData } from '@modules/professional/type/contact-page-types';
import { useIsomorphicLayoutEffect } from 'ahooks';

const ContactPageMobile = () => {
    const [isMapDisplayed, setIsMapDisplayed] = useState(false);
    const { css } = useStyles({ isMapDisplayed });
    const professional = useProfessionalInitialRouteData();
    const loaderData = useLoaderData<typeof loader>() as ContactLoaderData;

    const linkedProfiles = loaderData.linkedProfiles;

    const originalLocations = getLinkedProfileLocations(
        professional,
        linkedProfiles ?? {
            linkedProfilesCount: 0,
            linkedProfilesList: [],
            linkedProfilesProductSharing: [],
        },
    );

    const mapTopRef = useRef<HTMLDivElement>(null);
    const [searchParams] = useSearchParams();
    const urlShowMap = searchParams.get('showMap') === '1';
    const [showMap, setShowMap] = useState(urlShowMap);

    const {
        onSearchChange,
        onSearchClick,
        onMapMarkerHover,
        onMapMarkerClick,
        onMapPopupClose,
        calculateDistance,
        mapCenter,
        mapZoomNum,
        mapVariables,
        clickedLocation,
        hoveredLocation,
        filteredLocations,
        sortedLocations,
        hasDistance,
    } = useContactLocationMap(
        originalLocations,
        mapTopRef,
        true,
        professional.Country,
    );

    useIsomorphicLayoutEffect(() => {
        if (showMap) {
            if (SCROLL_TO_MAP_AFTER_CLICK_MAP_BUTTON) {
                window.scrollTo({
                    top: MOBILE_SEARCH_MAP_OFFSET,
                    behavior: 'smooth',
                });
            }

            const timeoutId = setTimeout(() => {
                setIsMapDisplayed(true);
            }, 500);

            document.body.style.touchAction = 'pan-x pan-y'; // no html page zoom (not map)

            return () => {
                clearTimeout(timeoutId);
                setIsMapDisplayed(false);
                document.body.style.touchAction = 'auto';
            };
        }
    }, [showMap]);

    useEffect(() => {
        mapTopRef.current?.scrollIntoView();
    }, [urlShowMap]);

    // For requirement: Map shows all pins if no search result
    const mapLocations =
        filteredLocations.length === 0 ? sortedLocations : filteredLocations;

    return (
        <div className={css(S.PageContent)}>
            <div className={css(S.Container)}>
                {sortedLocations.length > 0 && (
                    <ListMapNavTab
                        showMap={showMap}
                        onMapTabClick={setShowMap}
                        onSearchChange={onSearchChange}
                        onSearchClick={onSearchClick}
                    />
                )}
                {showMap && filteredLocations.length === 0 && (
                    <div className={css(ContentWrapper)}>
                        <NoSearchResult />
                    </div>
                )}
                {sortedLocations.length > 1 && !hasDistance && (
                    <FindNearest
                        numberOfLocations={sortedLocations.length}
                        calculateDistance={calculateDistance}
                    />
                )}
                {showMap ? (
                    <div className={css(ContentWrapper)}>
                        <div className={css(MapSection)} ref={mapTopRef}>
                            <LocationMap
                                center={mapCenter}
                                zoom={mapZoomNum}
                                filteredLocations={mapLocations}
                                mapVariables={mapVariables}
                                onMapMarkerHover={onMapMarkerHover}
                                onMapMarkerClick={onMapMarkerClick}
                                onMapPopupClose={onMapPopupClose}
                                hoveredLocation={hoveredLocation}
                                clickedLocation={clickedLocation}
                                linkedProfiles={linkedProfiles}
                            />
                        </div>
                    </div>
                ) : (
                    <LocationListMobile
                        locations={filteredLocations}
                        professional={professional}
                        originLocationCount={sortedLocations.length}
                    />
                )}
            </div>
        </div>
    );
};

export default ContactPageMobile;
